import { BreadcrumbItem, Breadcrumbs, Dropdown } from '@billy/design-system'
import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { FolderPathBreadcrumbItem } from '../../types/folderPathBreadcrumbItem'
import * as Styled from './styles'
import { getFolderName } from './utils/getFolderName'

interface FolderPathBreadcrumbsProps {
  folderPath: FolderPathBreadcrumbItem[]
  handleFolderClick: (folderId: string) => void
}

const MAX_VISIBLE_TAIL_CRUMBS = 2
const MIN_COLLAPSED_CRUMBS = 3

export const FolderPathBreadcrumbs = ({ folderPath, handleFolderClick }: FolderPathBreadcrumbsProps) => {
  const [isCollapsedCrumbsMenuOpen, setIsCollapsedCrumbsMenuOpen] = useState(false)
  const { t } = useTranslation()

  const openCollapsedCrumbsMenu = useCallback(() => setIsCollapsedCrumbsMenuOpen(true), [])
  const closeCollapsedCrumbsMenu = useCallback(() => setIsCollapsedCrumbsMenuOpen(false), [])

  const shouldTruncate = folderPath.length >= MIN_COLLAPSED_CRUMBS + MAX_VISIBLE_TAIL_CRUMBS
  const headCrumbs = shouldTruncate ? folderPath.slice(0, 1) : []
  const collapsedCrumbs = shouldTruncate ? folderPath.slice(1, folderPath.length - MAX_VISIBLE_TAIL_CRUMBS) : []
  const tailCrumbs = shouldTruncate ? folderPath.slice(folderPath.length - MAX_VISIBLE_TAIL_CRUMBS) : folderPath

  const renderBreadcrumbItem = (folder: FolderPathBreadcrumbItem, isLast: boolean) => (
    <BreadcrumbItem current={isLast} key={folder.id} id={folder.id} value={folder.id} onClick={handleFolderClick}>
      {isLast ? (
        <Styled.FolderPathItemCurrent>{getFolderName(folder, t)}</Styled.FolderPathItemCurrent>
      ) : (
        <Styled.FolderPathItemClickable>{getFolderName(folder, t)}</Styled.FolderPathItemClickable>
      )}
    </BreadcrumbItem>
  )

  return (
    <Styled.BreadcrumbsWrapper>
      <Breadcrumbs>
        {headCrumbs.map((folder) => renderBreadcrumbItem(folder, false))}
        {shouldTruncate && (
          <Dropdown
            isOpen={isCollapsedCrumbsMenuOpen}
            onOpen={openCollapsedCrumbsMenu}
            onClose={closeCollapsedCrumbsMenu}
            placement="bottom-start"
            trigger={<Styled.CollapsedCrumbsTrigger>…</Styled.CollapsedCrumbsTrigger>}
          >
            <Styled.CollapsedCrumbsMenu>
              {collapsedCrumbs.map((folder) => (
                <Styled.CollapsedCrumbsMenuItem
                  key={folder.id}
                  onClick={() => {
                    handleFolderClick(folder.id)
                    closeCollapsedCrumbsMenu()
                  }}
                >
                  {getFolderName(folder, t)}
                </Styled.CollapsedCrumbsMenuItem>
              ))}
            </Styled.CollapsedCrumbsMenu>
          </Dropdown>
        )}
        {tailCrumbs.map((folder, index) => {
          const isLast = index === tailCrumbs.length - 1

          return renderBreadcrumbItem(folder, isLast)
        })}
      </Breadcrumbs>
    </Styled.BreadcrumbsWrapper>
  )
}
